import * as CL from '@design-system/component-library';
import * as React from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { getElisaIdForgotPasswordUrl, paths } from '../../common/constants/pathVariables.js';
import { CommonErrorType } from '../../common/enums.js';
import {
  administratorsLegacyCredsMsg,
  authenticateMsg,
  closeMsg,
  customerServiceNumberOpenShortMsg,
  customerServiceNumberPriceMsg,
  elisaIdNoe1Msg,
  elisaIdNoe2Msg,
  elisaLogInMsg,
  elisaSignInMsg,
  forgottenPasswordMsg,
  hidepasswordMsg,
  logInMsg,
  loginCheckUserPassMsg,
  loginErrorHelpMsg,
  loginFailedMsg,
  newCustomerMsg,
  omaElisaAdminLoginMsg,
  omaElisaForCompaniesRegister2Msg,
  omaElisaForCompaniesRegister3Msg,
  omaElisaForCompaniesRegister4Msg,
  omaElisaForCompaniesRegister5Msg,
  omaElisaForCompaniesRegister6Msg,
  omaElisaForCompaniesRegisterMsg,
  omaElisaSignInPopupMsg,
  omaElisaSignInUsernameMsg,
  omaElisaWelcomeMsg,
  passwordMsg,
  passwordRequiredMsg,
  resetPasswordMsg,
  showpasswordMsg,
  t,
  technicalErrorMsg,
  usernameMsg,
} from '../../common/i18n/index.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useConfig } from '../../public/site/ConfigProvider.js';
import { CustomerServiceCallLink } from '../CustomerServicePhoneCallLink/CustomerServiceCallLink.js';
import { Icon } from '../Icon/Icon.js';
import { MfaOtp } from '../MFA/MfaOtp.js';
import { LoginDialog } from './LoginDialogs.js';
import type { AnonymousUser } from '../../public/site/authState.js';
import type { DialogData } from '../../public/site/dialogStore.js';
import type { JSX } from 'react';

import './MigrationLogin.scss';

interface MigrationLoginProps {
  dialog?: DialogData;
  defaultAccordionOpen?: boolean;
  includeAdmins?: boolean;
  compact?: boolean;
}

const getPossibleErrorMessage = (
  anonymousUserState?: AnonymousUser | null,
  dialogState?: DialogData
): JSX.Element | undefined => {
  if (dialogState?.submitInProgress || !anonymousUserState) {
    return undefined;
  }

  if (anonymousUserState.ssoSessionValid) {
    return undefined;
  }

  if (anonymousUserState.errors?.[0]?.type) {
    switch (anonymousUserState.errors[0].type) {
      case CommonErrorType.BAD_REQUEST:
      case CommonErrorType.FORBIDDEN:
      case CommonErrorType.VALIDATION:
        return (
          <>
            {t.O7AN(loginFailedMsg)} <br />
            {t.CLP6(loginCheckUserPassMsg)}
          </>
        );
      case CommonErrorType.SYSTEM:
        if (anonymousUserState.errors[0].source?.error) {
          // These are JIT's, missing-mobiles and such, will be handled with Dialogs, no need to show error.
          return undefined;
        }
        if (anonymousUserState.errors[0].source?.statusCode === 204) {
          // These are OEC redirects, no need to show error.
          return undefined;
        }
        return <>{t.T4OU(technicalErrorMsg)}</>;
      default:
        return undefined;
    }
  }
  return undefined;
};

const showErrorPopover = (anonymousUserState?: AnonymousUser | null, dialogState?: DialogData): boolean => {
  if (dialogState?.submitInProgress) {
    return false;
  }
  if (anonymousUserState) {
    if (anonymousUserState.ssoSessionValid) {
      return false;
    } else {
      switch (anonymousUserState.errors?.[0]?.type) {
        case CommonErrorType.BAD_REQUEST:
        case CommonErrorType.FORBIDDEN:
        case CommonErrorType.VALIDATION:
          return true;
        default:
          return false;
      }
    }
  }
  return false;
};

export const MigrationLogin = ({ dialog, defaultAccordionOpen, includeAdmins, compact }: MigrationLoginProps) => {
  const { login, anonymousUser, prepareElisaIdLogin } = useAuth();
  const { consumerMobiilivarmenneUrl } = useConfig();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [usernameMissing, setUsernameMissing] = React.useState(false);
  const [passwordMissing, setPasswordMissing] = React.useState(false);
  const [passwordAsClearText, setPasswordAsClearText] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const submitInProgress = dialog?.submitInProgress && dialog?.params?.type === undefined;

  React.useEffect(() => {
    setIsLoading(false);
  }, [anonymousUser]);

  const usernameHandler = (e: React.FormEvent<HTMLInputElement>) => {
    setUsername(e.currentTarget.value || '');
    setUsernameMissing(!e.currentTarget.value);
  };

  const passwordHandler = (e: React.FormEvent<HTMLInputElement>) => {
    setPassword(e.currentTarget.value || '');
    setPasswordMissing(!e.currentTarget.value);
  };

  if (anonymousUser?.mfaInfo) {
    return <MfaOtp phoneNumber={anonymousUser.mfaInfo.maskedPhone} />;
  }

  const errorMessage = getPossibleErrorMessage(anonymousUser, dialog);
  const shouldShowPopover = showErrorPopover(anonymousUser, dialog);

  return (
    <div id="of-self-service-noe-parent" className={compact ? '' : `of-self-service-login-scene__top-noe`}>
      {dialog && (
        <LoginDialog dialog={dialog} userName={username} setUserName={setUsername} setPassword={setPassword} />
      )}
      <CL.Grid className={compact ? '' : `${dsClass.PADDING_TOP_2}`}>
        <CL.GridRow justifyCenter={!compact} className={compact ? dsClass.PADDING_0 : undefined}>
          {!compact && (
            <CL.GridCol colWidthXS={4} colWidthS={6} colWidthL={9} colWidthXL={9} className={dsClass.PADDING_2}>
              <div className={`${dsClass.TEXT_ALIGN_CENTER}`}>
                <h1 className={dsClass.MARGIN_TOP_2}>{t.YK5V(omaElisaWelcomeMsg)}</h1>
                <p className={`${dsClass.FONT_WEIGHT_400} ${dsClass.FONT_SIZE_LARGE}`}>{t.GPPO(elisaIdNoe1Msg)}</p>
              </div>
            </CL.GridCol>
          )}
          <CL.GridCol
            colWidthXS={4}
            colWidthS={6}
            colWidthL={compact ? 7 : 6}
            colWidthXL={compact ? 7 : 6}
            className={compact ? `${dsClass.PADDING_0}` : `${dsClass.PADDING_BOTTOM_8}`}
          >
            <div className={`of-migration-login of-migration-login__root ${compact ? '' : dsClass.PADDING_VERTICAL_2}`}>
              <div className="ds-container">
                <div className="of-migration-login__body">
                  <div
                    className={`of-migration-login-cards-container ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
                  >
                    {/* Primary Elisa ID Login Section */}
                    <CL.Card className={`of-migration-login-card ${dsClass.PADDING_4} ${dsClass.TEXT_ALIGN_CENTER}`}>
                      <div
                        className={`of-migration-login-elisa-id ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
                      >
                        <div className={`of-migration-login-elisa-id__icon ${dsClass.DISPLAY_BLOCK}`} />
                        <h2 className={`${dsClass.MARGIN_0}`}>{t.M76W(elisaSignInMsg)}</h2>
                        <p className={`${dsClass.MARGIN_0}`}>{t.D00E(elisaIdNoe2Msg)}</p>
                        <CL.Button
                          size="l"
                          onClick={() => {
                            prepareElisaIdLogin({ includeAdmins });
                          }}
                        >
                          <>
                            {t.M76W(elisaLogInMsg)} <CL.Icon icon="arrow-right-alt" type="filled" />
                          </>
                        </CL.Button>
                        {consumerMobiilivarmenneUrl && (
                          <CL.Link
                            linkStyle="link"
                            className={`${dsClass.TEXT_M} ${dsClass.FONT_WEIGHT_400}`}
                            linkHref={getElisaIdForgotPasswordUrl(consumerMobiilivarmenneUrl)}
                          >
                            {t.C5T9('Forgotten your Elisa ID password?')}
                          </CL.Link>
                        )}
                      </div>
                    </CL.Card>

                    {/* Legacy Username/Password Login - Accordion */}
                    <CL.Card className={`of-migration-login-card ${dsClass.PADDING_4} ${dsClass.MARGIN_0}`}>
                      <CL.Accordion className="of-migration-login-accordion">
                        <CL.AccordionItem
                          defaultOpen={defaultAccordionOpen}
                          heading={
                            <div
                              className={`of-migration-login-accordion-header ${dsClass.MARGIN_0} ${dsClass.PADDING_0}`}
                            >
                              <div className="of-migration-login-accordion-header-icon">
                                <CL.Icon icon="authorization" type="filled" />
                              </div>
                              <div className="of-migration-login-accordion-header-text">
                                <h3 className={`${dsClass.H3} ${dsClass.MARGIN_0}`}>
                                  {t.HPSA(omaElisaSignInUsernameMsg)}
                                </h3>
                                <p className={`of-migration-login-accordion-expanded-subtitle ${dsClass.MARGIN_0}`}>
                                  {t.TM4Y(administratorsLegacyCredsMsg)}
                                </p>
                              </div>
                            </div>
                          }
                          headingLevel="div"
                          headingSize="m"
                        >
                          <form
                            onSubmit={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              setIsLoading(true);
                              login(username, password);
                            }}
                          >
                            <div
                              className={`of-migration-login-accordion-expanded ${dsClass.DISPLAY_FLEX} ${dsClass.MARGIN_TOP_2}`}
                            >
                              <p className="of-migration-login-accordion-expanded-subtitle">
                                {t.TM4Z(omaElisaAdminLoginMsg)}
                              </p>

                              <div
                                className={`of-migration-login-accordion-expanded-loginblock ${dsClass.DISPLAY_FLEX}`}
                              >
                                <div
                                  className={`of-migration-login-accordion-expanded-loginblock-userpass ${dsClass.DISPLAY_FLEX}`}
                                >
                                  {(errorMessage || shouldShowPopover) && (
                                    <div className={`${dsClass.INPUTERROR} ${dsClass.DISPLAY_BLOCK}`}>
                                      {errorMessage}{' '}
                                      {shouldShowPopover && (
                                        <CL.Popover
                                          triggerElement={
                                            <Icon color="red-600" icon="information" size="s" type="regular" />
                                          }
                                          placement="right"
                                          defaultOpen={true}
                                          className={`of-migration-login-error-popover ${dsClass.WIDTH_100}`}
                                          i18n_popover_closeButtonTitle={t.WOYD(closeMsg)}
                                        >
                                          <p className={dsClass.FONT_SIZE_LARGE}>
                                            <strong>{`${t.I3A5(loginErrorHelpMsg)}`}</strong>
                                          </p>
                                          <ol className={dsClass.FONT_SIZE_LARGE}>
                                            <li>
                                              <CL.Link linkStyle="link" linkHref={paths.FORGOT_PASSWORD}>
                                                {t.Z3W8(resetPasswordMsg)}
                                              </CL.Link>
                                            </li>
                                            <li>
                                              <a href={paths.REGISTER_PAGE}>
                                                {t.CKF5(omaElisaForCompaniesRegisterMsg)}
                                              </a>{' '}
                                              {t.EZYP(omaElisaForCompaniesRegister2Msg)}
                                            </li>
                                            <li>
                                              {t.ZRR4(omaElisaForCompaniesRegister3Msg)}{' '}
                                              <a href="https://verkkoasiointi.elisa.fi/">
                                                {t.D1YH(omaElisaForCompaniesRegister4Msg)}
                                              </a>
                                              {t.UZFU(omaElisaForCompaniesRegister5Msg)}
                                            </li>
                                            <li>
                                              {t.C3XX(omaElisaForCompaniesRegister6Msg)} <CustomerServiceCallLink />
                                              .
                                              <br />
                                              <small>
                                                ({t.HQWU(customerServiceNumberOpenShortMsg)},{' '}
                                                {t.HQWW(customerServiceNumberPriceMsg)})
                                              </small>
                                            </li>
                                          </ol>
                                        </CL.Popover>
                                      )}
                                    </div>
                                  )}
                                  <div id="username-input" className={`${dsClass.INPUT} ${dsClass.MARGIN_0}`}>
                                    <div className={dsClass.INPUT_LABELAREA}>
                                      <label className={dsClass.INPUT_LABELAREA_LABEL} htmlFor="username">
                                        {t.KV4P(usernameMsg)}
                                      </label>

                                      <div className={`${dsClass.INPUT} ${dsClass.DISPLAY_INLINE}`}>
                                        <CL.Popover
                                          triggerElement={
                                            <Icon color="black" icon="information" size="s" type="regular" />
                                          }
                                          placement="top"
                                          defaultOpen={false}
                                          className="of-migration-login-info-popover"
                                          i18n_popover_closeButtonTitle={t.WOYD(closeMsg)}
                                        >
                                          <p>{t.DRKV(omaElisaSignInPopupMsg)}</p>
                                        </CL.Popover>
                                      </div>
                                    </div>

                                    <div className={dsClass.INPUT_INPUTAREA}>
                                      <input
                                        id="username"
                                        className={dsClass.INPUT_INPUTAREA_INPUT}
                                        name="username"
                                        type="text"
                                        placeholder={t.KV4P(usernameMsg)}
                                        onChange={usernameHandler}
                                        disabled={submitInProgress}
                                        value={username}
                                      />
                                    </div>

                                    {usernameMissing && (
                                      <div className={dsClass.INPUTERROR}>{t.SMI6('User ID is required')}</div>
                                    )}
                                  </div>
                                  <div className={`${dsClass.INPUT} ${dsClass.INPUT_PASSWORD} ${dsClass.MARGIN_0}`}>
                                    <div className={dsClass.INPUT_LABELAREA}>
                                      <label className={dsClass.INPUT_LABELAREA_LABEL} htmlFor="password-input">
                                        {t.HCCZ(passwordMsg)}
                                      </label>
                                    </div>
                                    <div className={`${dsClass.INPUT_INPUTAREA} of-password-input`}>
                                      <input
                                        id="password-input"
                                        className={dsClass.INPUT_INPUTAREA_INPUT}
                                        name="password"
                                        type={passwordAsClearText ? 'text' : 'password'}
                                        placeholder={t.HCCZ(passwordMsg)}
                                        onChange={passwordHandler}
                                        disabled={submitInProgress}
                                        value={password}
                                      />
                                      <button
                                        className={dsClass.INPUT_INPUTAREA_VIEWBUTTON}
                                        tabIndex={0}
                                        type="button"
                                        aria-label={
                                          passwordAsClearText ? t.S4FM(hidepasswordMsg) : t.F09D(showpasswordMsg)
                                        }
                                        onClick={() => setPasswordAsClearText(!passwordAsClearText)}
                                      >
                                        <span
                                          title={
                                            passwordAsClearText ? t.S4FM(hidepasswordMsg) : t.F09D(showpasswordMsg)
                                          }
                                          aria-hidden
                                        />
                                        {passwordAsClearText ? (
                                          <CL.Icon key="view" icon="view" type="filled" />
                                        ) : (
                                          <CL.Icon key="view-off" icon="view-off" type="filled" />
                                        )}
                                      </button>
                                    </div>
                                    {passwordMissing && (
                                      <div className={dsClass.INPUTERROR}>{t.ATDL(passwordRequiredMsg)}</div>
                                    )}
                                  </div>
                                </div>

                                <CL.Button
                                  id="login-submit-button"
                                  size="l"
                                  block
                                  disabled={!username || !password}
                                  type="submit"
                                  loading={isLoading}
                                >
                                  {t.Z16I(logInMsg)}
                                </CL.Button>
                              </div>

                              <CL.Link linkStyle="link" linkHref={paths.FORGOT_PASSWORD}>
                                {t.EAFO(forgottenPasswordMsg)}
                              </CL.Link>
                            </div>
                          </form>
                        </CL.AccordionItem>
                      </CL.Accordion>
                    </CL.Card>

                    {/* New Customer / Authentication Section */}
                    <CL.Card className={`of-migration-login-card ${dsClass.PADDING_4}`}>
                      <div
                        className={`of-migration-login-newuser ${dsClass.DISPLAY_FLEX} ${dsClass.DISPLAY_BLOCK} ${dsClass.TEXT_ALIGN_CENTER} ${dsClass.ALIGN_ITEMS_CENTER}`}
                      >
                        <div
                          className={`of-migration-login-newuser-title ${dsClass.DISPLAY_FLEX} ${dsClass.DISPLAY_BLOCK}`}
                        >
                          <h3 className={`${dsClass.H3} ${dsClass.MARGIN_0}`}>{t.KNM4(newCustomerMsg)}</h3>
                          <p className={`of-migration-login-accordion-expanded-subtitle ${dsClass.MARGIN_0}`}>
                            {t.CGVI(
                              'If you have signature rights to the company, you can proceed by authenticating yourself.'
                            )}
                          </p>
                        </div>
                        <CL.Button
                          color="secondary"
                          size="m"
                          className="of-migration-login-white-button"
                          onClick={() => {
                            prepareElisaIdLogin({ register: true });
                          }}
                        >
                          {t.CMMT(authenticateMsg)}
                        </CL.Button>
                      </div>
                    </CL.Card>
                  </div>
                </div>
              </div>
            </div>
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>
    </div>
  );
};
